import React from 'react';
import { Col, Divider, Grid, Row, Tabs } from 'antd';

import { useAttachFile } from '../../common/useAttachFile';
import { StyledRow } from '../../components/StyledRow';
import Icon from '../../icons/Icon';
import { CandidateType } from '../candidates/Candidates.types';
import { RecruitmentHistoryType } from '../recruitments/Recruitments.types';

import { Candidate } from './Candidate';
import { HRReview } from './HRReview';
import { RecruitmentDataForm } from './RecruitmentDataForm';
import { RecruitmentHistory } from './RecruitmentHistory';
import { TechnicalReview } from './TechnicalReview';

type RecruitmentStatusDataProps = {
  recruitment: RecruitmentHistoryType;
  candidate: CandidateType;
  height?: number;
};

export const RecruitmentStatus: React.FC<RecruitmentStatusDataProps> = ({ recruitment, candidate, height = 1025 }) => {
  const screens = Grid.useBreakpoint();

  const hrInterviewAttachements = candidate.files.filter((file) => file.type === 'HR_INTERVIEW') || [];

  const technicalInterviewAttachements = candidate.files.filter((file) => file.type === 'TECHNICAL_INTERVIEW') || [];

  const { attachHrInterviewFile, attachTechnicalInterviewFile } = useAttachFile(candidate.uuid);

  const isHrReview = candidate.hrInterviewAssessment || candidate.hrInterviewDate || candidate.hrInterviewSummary;
  const isTechnicalReview =
    candidate.technicalInterviewAssessment || candidate.technicalInterviewDate || candidate.technicalInterviewSummary;

  const items = [
    {
      key: '1',
      label: 'History',
      children: <RecruitmentHistory recruitment={recruitment} />,
    },
    {
      key: '2',
      label: 'Candidate',
      children: <Candidate candidate={candidate} height={height} />,
    },
    {
      key: '3',
      label: (
        <StyledRow gap="5px">
          <div style={{ marginRight: '5px' }}> HR review</div>
          {isHrReview && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name="HrReview" inlineStyle />
            </div>
          )}
        </StyledRow>
      ),
      children: (
        <HRReview
          candidate={candidate}
          standAloneMode={true}
          hrInterviewAttachements={hrInterviewAttachements}
          attachFile={attachHrInterviewFile}
        />
      ),
    },
    {
      key: '4',
      label: (
        <StyledRow gap="5px">
          <div style={{ marginRight: '5px' }}> Technical review</div>
          {isTechnicalReview && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name="HrReview" />
            </div>
          )}
        </StyledRow>
      ),
      children: (
        <TechnicalReview
          candidate={candidate}
          standAloneMode={true}
          technicalInterviewAttachements={technicalInterviewAttachements}
          attachFile={attachTechnicalInterviewFile}
        />
      ),
    },
  ];

  return (
    <Row gutter={[15, 15]}>
      <Col xs={24} xl={6}>
        <RecruitmentDataForm candidate={candidate} recruitment={recruitment} />
      </Col>

      {screens.xl ? (
        <div style={{ width: '20px', display: 'flex', justifyContent: 'center' }}>
          <Divider type="vertical" style={{ height: '100%' }} />
        </div>
      ) : (
        <Divider style={{ margin: '0' }} />
      )}

      <Col xs={24} style={{ flex: 1 }}>
        <Tabs defaultActiveKey="1" items={items} />
      </Col>
    </Row>
  );
};
